import { Button, message } from 'antd';
import { ApiOutlined, LoadingOutlined } from '@ant-design/icons';
import type { ConnectionTestResult } from './types';

interface ConnectionTestButtonProps<T> {
    getConfig: () => T | null;
    testConnection: (config: T) => Promise<ConnectionTestResult>;
    isLoading: boolean;
    requiredFieldsMessage?: string;
}

export default function ConnectionTestButton<T>({
    getConfig,
    testConnection,
    isLoading,
    requiredFieldsMessage = 'Please fill in all required fields before testing the connection',
}: ConnectionTestButtonProps<T>) {
    const handleTest = async () => {
        const config = getConfig();
        if (!config) {
            message.warning(requiredFieldsMessage);
            return;
        }

        try {
            const testResult = await testConnection(config);

            const resources = testResult.available_resources || testResult.resources;
            if (testResult.success) {
                message.success({
                    content: `Connection successful!${resources?.length ? ` Found ${resources.length} resources` : ''}`,
                    duration: 3,
                });
            } else {
                message.error({
                    content: testResult.message || 'Connection failed',
                    duration: 5,
                    key: 'connection-test-error',
                });
            }
        } catch (error: unknown) {
            const err = error as { data?: { detail?: string }; message?: string };
            const errorMessage = err?.data?.detail || err?.message || 'Connection test failed';
            message.error({
                content: errorMessage,
                duration: 5,
                key: 'connection-test-error',
            });
        }
    };

    return (
        <div className="mb-4">
            <Button
                type="primary"
                ghost
                onClick={handleTest}
                loading={isLoading}
                icon={isLoading ? <LoadingOutlined /> : <ApiOutlined />}
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                data-testid="add-source-test-connection-button"
            >
                {isLoading ? 'Testing Connection...' : 'Test Connection'}
            </Button>
        </div>
    );
}
